import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LANGUAGES, LanguageCode } from "../constants/languages";
import i18n from "../i18n/config";

interface LanguageSwitchProps {
  currentLang?: string;
}

const LanguageSwitch: React.FC<LanguageSwitchProps> = ({
  currentLang = "en",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(
    currentLang as LanguageCode
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedLanguage(currentLang as LanguageCode);
  }, [currentLang]);

  useEffect(() => {
    const pathLanguage = location.pathname.split("/")?.[1] || "";
    if (pathLanguage.length) {
      setSelectedLanguage(pathLanguage as LanguageCode);
    }
  });

  const currentLanguage = LANGUAGES[selectedLanguage] || LANGUAGES.en;

  const changeLanguage = (languageCode: LanguageCode) => {
    const path = location.pathname;

    if (path === `/${selectedLanguage}`) {
      navigate(`/${languageCode}`);
    } else {
      const newPath = path.replace(`/${selectedLanguage}`, `/${languageCode}`);
      navigate(newPath);
    }

    setSelectedLanguage(languageCode);
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (location.pathname.includes("/admin")) {
    return null;
  }

  return (
    <div className="absolute right-4 inline-block z-50" ref={dropdownRef}>
      <button
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select language"
      >
        <span className="text-xl">{currentLanguage.flag}</span>
        <span className="font-medium text-sm text-gray-700 hidden sm:inline">
          {currentLanguage.name}
        </span>
        <span className="text-xs text-gray-400 ml-1">{isOpen ? "▲" : "▼"}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {Object.values(LANGUAGES).map((language) => (
            <button
              key={language.code}
              className={`
                w-full px-4 py-2 text-left flex items-center gap-2 
                ${
                  language.code === selectedLanguage
                    ? "bg-gray-100 cursor-default"
                    : "hover:bg-gray-50"
                }
                transition-colors duration-150 ease-in-out
              `}
              onClick={() => changeLanguage(language.code)}
              disabled={language.code === selectedLanguage}
            >
              <span className="text-xl">{language.flag}</span>
              <span className="font-medium text-sm text-gray-700">
                {language.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitch;
